import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { readSelectedModel, writeSelectedModel } from "../../../chat/modelPreference";
import { ChatClient, type ChatClientOptions } from "../../../chat/client";
import { useChatSession } from "../../../chat/hooks/useChatSession";
import { useSessions } from "../../../chat/hooks/useSessions";
import { TurnStatus } from "../../../chat/hooks/useStreamingTurn";
import { useProposals, type UseProposalsResult } from "../../../chat/hooks/useProposals";
import { useGranolaPending, type UseGranolaPendingResult } from "../../../chat/hooks/useGranolaPending";
import { useNotificationsOptional } from "../../../notifications/NotificationsProvider";
import { RestClient } from "../../../api/restClient";
import { ProposalsApi } from "../../../api/proposalsApi";
import { ArtifactsApi } from "../../../api/artifactsApi";
import { DocumentsApi } from "../../../api/documentsApi";
import { GranolaApi } from "../../../api/granolaApi";
import { useEmbedApiAuth, makeBearerFetch } from "../../../api/embedApiContext";
import {
  createFrameRegistry,
  FrameRendererMap,
  FrameRegistry,
} from "../../../chat/extensions/frameRegistry";
import { EMPTY_SLOTS, type ChatSlots } from "../../../chat/extensions/slots";
import {
  ChatPanelFeatures,
  DEFAULT_FEATURES,
} from "../../../chat/types";
import { ThemeBridge } from "./ThemeBridge";

export interface ChatProviderProps {
  apiUrl: string;
  /** Extra ChatClient options. The embed widget passes embed-mode config here
   *  (extraHeaders X-Embed-Key, getAuthToken, chatPath /embed/v1/chat,
   *  credentials "omit"). Omitted by the SPA → default cookie/BFF behavior. */
  clientOptions?: Partial<Omit<ChatClientOptions, "apiUrl">>;
  initialSessionId?: string | null;
  /** Pre-select a project for a brand-new chat (e.g. opening chat from a
   *  project page via ?project_id=…). Threaded into the first send so the
   *  session is created inside the project. */
  initialProjectId?: string | null;
  defaultModel?: string;
  features?: Partial<ChatPanelFeatures>;
  slots?: ChatSlots;
  frameRenderers?: FrameRendererMap;
  onSessionChange?: (id: string | null) => void;
  onEvent?: (frame: any) => void;
  children: ReactNode;
}

export interface ChatContextValue {
  client: ChatClient;
  sessionId: string | null;
  setSessionId: (id: string | null) => void;
  sessions: ReturnType<typeof useSessions>;
  chat: ReturnType<typeof useChatSession>;
  registry: FrameRegistry;
  slots: ChatSlots;
  features: ChatPanelFeatures;
  defaultModel: string | undefined;
  /** Per-session user-selected model id, or "auto" for router-driven.
   *  The composer reads this and threads it into sendMessage; "auto"
   *  means don't send a model field at all so the backend's existing
   *  router / construction-time default kicks in. */
  selectedModel: string;
  setSelectedModel: (id: string) => void;
  /** Project the current session is associated with. For a brand-new
   *  session (sessionId === null) this is the project the user chose
   *  before sending the first message; the Composer threads it into the
   *  first POST so the backend can set it at session-creation time. */
  projectId: string | null;
  setProjectId: (id: string | null) => void;
  artifactsApi: ArtifactsApi;
  /** REST client for document upload + status polling (attachments). */
  documentsApi: DocumentsApi;
  /** Out-of-band proposals (from Phase 1 background tasks / cross-session
   *  worker). Exposed so any child can badge-count, open the tray, or
   *  call refreshAfterTurn() after a turn completes. */
  proposals: UseProposalsResult;
  /** Meetings staged from a Granola webhook awaiting an accept/dismiss
   *  decision (see granola_webhook_routes.py). Backs MeetingKbBanner. */
  granolaPending: UseGranolaPendingResult;
  /** A one-shot draft pushed into the Composer (e.g. clicking a suggested
   *  prompt on the empty screen prefills the input so the user can edit
   *  before sending). The Composer consumes it into its local text state
   *  and clears it. `prefillNonce` bumps on every set so the same text can
   *  be re-applied. */
  composerDraft: string;
  prefillNonce: number;
  setComposerDraft: (text: string) => void;
}

/** The slice of the chat context that does not change while a turn streams.
 *  Components rendered once per history message (artifact cards, media cards,
 *  markdown table menus) read this instead of the full context: the full value
 *  carries `chat`, which changes on every streamed flush, and re-rendering
 *  every card per token stalled the page on big invoice reviews
 *  (ELASV1-3514). */
export type ChatServicesValue = Pick<
  ChatContextValue,
  | "client"
  | "sessionId"
  | "registry"
  | "slots"
  | "features"
  | "artifactsApi"
  | "documentsApi"
>;

const EMPTY_FRAME_RENDERERS: FrameRendererMap = {};

const ChatContext = createContext<ChatContextValue | null>(null);
const ChatServicesContext = createContext<ChatServicesValue | null>(null);

export function useChatServices(): ChatServicesValue {
  const ctx = useContext(ChatServicesContext);
  if (!ctx) throw new Error("useChatServices must be used inside <ChatProvider>");
  return ctx;
}

/** Non-throwing variant, for components that also mount outside the chat. */
export function useChatServicesOptional(): ChatServicesValue | null {
  return useContext(ChatServicesContext);
}

export function useChatContext(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatContext must be used inside <ChatProvider>");
  return ctx;
}

/** Non-throwing variant — returns null when there's no provider. The markdown
 *  renderer and the artifact cards also mount outside the chat tree (artifact
 *  previews, subagent results, standalone tests), so anything reading session
 *  provenance from that depth has to degrade rather than crash the subtree.
 *  Same shape as useNotificationsOptional. */
export function useChatContextOptional(): ChatContextValue | null {
  return useContext(ChatContext);
}

export function ChatProvider({
  apiUrl,
  clientOptions,
  initialSessionId = null,
  initialProjectId = null,
  defaultModel,
  features,
  slots = EMPTY_SLOTS,
  frameRenderers = EMPTY_FRAME_RENDERERS,
  onSessionChange,
  onEvent,
  children,
}: ChatProviderProps) {
  const client = useMemo(
    () => new ChatClient({ apiUrl, ...(clientOptions as Partial<ChatClientOptions>) }),
    // clientOptions is a stable object from the widget mount; re-memo on apiUrl.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiUrl]
  );
  // Backend feature flags. Fetched once so the UI only offers controls the
  // backend honors (e.g. the Plan toggle was a silent no-op when plan mode was
  // disabled server-side). null until loaded → fall back to client defaults.
  const [backendFeatures, setBackendFeatures] = useState<{
    plan_mode: boolean;
    background_subagents: boolean;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    client.getConfig().then((cfg) => {
      if (!cancelled && cfg) setBackendFeatures(cfg.features);
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const [sessionId, setSessionIdState] = useState<string | null>(initialSessionId);
  const onSessionChangeRef = useRef(onSessionChange);
  onSessionChangeRef.current = onSessionChange;
  const setSessionId = useCallback((id: string | null) => {
    setSessionIdState(id);
    onSessionChangeRef.current?.(id);
  }, []);
  // Last-used model picker choice, persisted (not per-session) so it survives
  // reloads/remounts and rides on every message. Lazy-init from storage; the
  // wrapped setter keeps storage in sync. "auto" = send no model field.
  const [selectedModel, setSelectedModelState] = useState<string>(readSelectedModel);
  const setSelectedModel = useCallback((id: string) => {
    setSelectedModelState(id);
    writeSelectedModel(id);
  }, []);
  const [projectId, setProjectId] = useState<string | null>(initialProjectId);
  // One-shot composer prefill (suggested prompts on the empty screen). The
  // nonce lets the Composer re-apply the same text twice in a row.
  const [composerDraft, setComposerDraftState] = useState("");
  const [prefillNonce, setPrefillNonce] = useState(0);
  const setComposerDraft = useCallback((text: string) => {
    setComposerDraftState(text);
    setPrefillNonce((n) => n + 1);
  }, []);
  const registry = useMemo(() => createFrameRegistry(frameRenderers), [frameRenderers]);
  const sessions = useSessions(client);

  // The panel's REST clients must authenticate the same way the workspace
  // pages do (see useAuthedApi): the SPA rides the HttpOnly `egpt_session`
  // cookie, but the rich embed is cross-origin and has no cookie — it holds a
  // Bearer userJWT. Building these with RestClient's default (cookie) fetch
  // made every /core/* call from the embedded panel 401 "missing or invalid
  // credentials" — most visibly on document upload. Null in the SPA, so the
  // cookie path is unchanged there.
  const embedApiAuth = useEmbedApiAuth();
  const embedFetch = useMemo(
    () => (embedApiAuth ? makeBearerFetch(embedApiAuth.getToken) : undefined),
    [embedApiAuth],
  );

  const proposalsApi = useMemo(() => {
    const rest = new RestClient({ apiUrl, fetch: embedFetch });
    return new ProposalsApi(rest);
  }, [apiUrl, embedFetch]);

  const artifactsApi = useMemo(() => {
    const rest = new RestClient({ apiUrl, fetch: embedFetch });
    return new ArtifactsApi(rest);
  }, [apiUrl, embedFetch]);

  const documentsApi = useMemo(() => {
    const rest = new RestClient({ apiUrl, fetch: embedFetch });
    return new DocumentsApi(rest);
  }, [apiUrl, embedFetch]);

  const granolaApi = useMemo(() => {
    const rest = new RestClient({ apiUrl, fetch: embedFetch });
    return new GranolaApi(rest);
  }, [apiUrl, embedFetch]);

  const chat = useChatSession({
    client,
    sessionId,
    registry,
    onEvent,
    proposalsApi,
    onSessionEstablished: setSessionId,
    // Thread the picked model onto every turn-start path (send, regenerate,
    // edit) so an explicit selection is honoured on every message — not just
    // the first send. Without this, regenerate/edit dropped the model and the
    // backend silently fell back to its cost router (auto mode).
    selectedModel,
  });

  // Refresh the sessions list after each completed turn so newly-created
  // sessions and backend-generated titles appear in the sidebar without a
  // full page reload.  The backend generates the title asynchronously (~1-2s
  // after the turn completes), so we reload immediately (to surface the new
  // session entry) and once more after a short delay (to pick up the title).
  const lastTurnStatusRef = useRef(chat.turn.status);
  useEffect(() => {
    const prev = lastTurnStatusRef.current;
    lastTurnStatusRef.current = chat.turn.status;
    if (chat.turn.status === TurnStatus.Complete && prev !== TurnStatus.Complete) {
      sessions.reload();
      // The title is generated by a fire-and-forget Haiku call that lands a
      // couple seconds after the turn completes; two staggered reloads give it
      // margin without polling (first surfaces a new session, later picks up
      // the title once persisted).
      const t1 = setTimeout(() => sessions.reload(), 2500);
      const t2 = setTimeout(() => sessions.reload(), 6000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [chat.turn.status, sessions.reload]);

  // Background subagent → chat injection: when a subagent_run job finishes for
  // THIS session, the worker has already persisted its answer as a SubagentCard
  // message; refetch history so it appears in the thread without a reload. The
  // event arrives on the shared notification SSE (no second EventSource).
  const notifications = useNotificationsOptional();
  const reloadHistory = chat.reloadHistory;
  useEffect(() => {
    if (!notifications || !sessionId) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const unsub = notifications.subscribe((ev) => {
      const p = (ev.payload ?? {}) as Record<string, unknown>;
      if (
        ev.type === "job_event" &&
        p.job_type === "subagent_run" &&
        p.status === "succeeded" &&
        p.session_id === sessionId
      ) {
        void reloadHistory();
        // The succeeded NOTIFY races the worker's message INSERT becoming
        // visible through the agent's in-process history cache — the first
        // refetch can miss the just-persisted card. One delayed refetch
        // closes that window (observed live: first fetch stale, 1s later
        // fetch has the row).
        timers.push(setTimeout(() => void reloadHistory(), 1500));
      }
    });
    return () => {
      timers.forEach(clearTimeout);
      unsub();
    };
  }, [notifications, sessionId, reloadHistory]);

  // SP2: fire-and-forget FactsExtractor publishes facts_updated to the events
  // bus AFTER the SSE turn stream closes, so the Memory-updated pill must be
  // bumped out-of-band rather than from the in-stream facts_updated frame.
  // We call bumpFactsUpdatedAt (exposed by useStreamingTurn) which dispatches
  // the same synthetic FactsUpdated frame the in-stream path uses.
  const bumpFactsUpdatedAt = chat.turn.bumpFactsUpdatedAt;
  useEffect(() => {
    if (!notifications) return;
    return notifications.subscribe((ev) => {
      if (
        ev.type === "facts_updated" &&
        (ev.payload as Record<string, unknown>)?.session_id === sessionId
      ) {
        bumpFactsUpdatedAt();
      }
    });
  }, [notifications, bumpFactsUpdatedAt, sessionId]);

  // Out-of-band proposals (background tasks, cross-session worker).
  // Separate from the inline proposals already handled by useChatSession.
  const proposalsResult = useProposals(proposalsApi);

  // Meetings staged from a Granola webhook, awaiting an accept/dismiss
  // decision in MeetingKbBanner.
  const granolaPendingResult = useGranolaPending(granolaApi);

  // Memoized on its inputs: an embedder typically passes `features` as an
  // inline object, and a fresh object here would change the services value
  // below on every render. Keyed on the serialized prop for the same reason.
  const featuresKey = JSON.stringify(features ?? {});
  const mergedFeatures: ChatPanelFeatures = useMemo(() => {
    const merged: ChatPanelFeatures = { ...DEFAULT_FEATURES, ...(features ?? {}) };
    // Once the backend reports its capabilities, gate backend-dependent toggles
    // on them so the UI never shows a control the server will ignore. (Until the
    // fetch resolves we keep the client defaults; an explicit prop override in
    // `features` still wins for embedders that force a value.)
    if (backendFeatures && (features?.planMode === undefined)) {
      merged.planMode = backendFeatures.plan_mode;
    }
    if (backendFeatures && (features?.backgroundSubagents === undefined)) {
      merged.backgroundSubagents = backendFeatures.background_subagents;
    }
    return merged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresKey, backendFeatures]);

  const services: ChatServicesValue = useMemo(
    () => ({
      client,
      sessionId,
      registry,
      slots,
      features: mergedFeatures,
      artifactsApi,
      documentsApi,
    }),
    [client, sessionId, registry, slots, mergedFeatures, artifactsApi, documentsApi],
  );

  // Changes whenever `chat` does (every streamed flush); what must not
  // re-render per token reads `services` instead.
  const value: ChatContextValue = useMemo(
    () => ({
      ...services,
      setSessionId,
      sessions,
      chat,
      defaultModel,
      selectedModel,
      setSelectedModel,
      projectId,
      setProjectId,
      proposals: proposalsResult,
      granolaPending: granolaPendingResult,
      composerDraft,
      prefillNonce,
      setComposerDraft,
    }),
    [
      services,
      setSessionId,
      sessions,
      chat,
      defaultModel,
      selectedModel,
      setSelectedModel,
      projectId,
      proposalsResult,
      granolaPendingResult,
      composerDraft,
      prefillNonce,
      setComposerDraft,
    ],
  );

  return (
    <ThemeBridge>
      <ChatServicesContext.Provider value={services}>
        <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
      </ChatServicesContext.Provider>
    </ThemeBridge>
  );
}
